/**
 * Add a vehicle, or change its details. Only the plate is required: the office often
 * knows the plate on day one and the chassis number when the paperwork turns up.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { Field } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, ApiError, type Vehicle, type VehicleFuel, type VehicleInput } from '@/lib/api';

const FUELS: VehicleFuel[] = ['diesel', 'petrol', 'hybrid', 'electric', 'lpg', 'methane'];

interface Form {
  plate: string;
  name: string;
  make: string;
  model: string;
  year: string;
  vin: string;
  fuel: '' | VehicleFuel;
  registeredOn: string;
  odometerKm: string;
  notes: string;
}

const fromVehicle = (v?: Vehicle | null): Form => ({
  plate: v?.plate ?? '',
  name: v?.name ?? '',
  make: v?.make ?? '',
  model: v?.model ?? '',
  year: v?.year ? String(v.year) : '',
  vin: v?.vin ?? '',
  fuel: v?.fuel ?? '',
  registeredOn: v?.registeredOn ?? '',
  odometerKm: v?.odometerKm !== null && v?.odometerKm !== undefined ? String(v.odometerKm) : '',
  notes: v?.notes ?? '',
});

export function VehicleForm({
  open,
  onClose,
  vehicle,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** Present when editing. */
  vehicle?: Vehicle | null;
  onSaved?: (v: Vehicle) => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Form>(fromVehicle(vehicle));

  useEffect(() => {
    if (open) setForm(fromVehicle(vehicle));
  }, [open, vehicle]);

  const save = useMutation({
    mutationFn: async () => {
      const body: VehicleInput = {
        plate: form.plate,
        name: form.name.trim() || null,
        make: form.make.trim() || null,
        model: form.model.trim() || null,
        year: form.year ? Number(form.year) : null,
        vin: form.vin.trim() || null,
        fuel: form.fuel || null,
        registeredOn: form.registeredOn || null,
        odometerKm: form.odometerKm ? Number(form.odometerKm) : null,
        notes: form.notes.trim() || null,
      };
      return vehicle ? api.updateVehicle(vehicle.id, body) : api.createVehicle(body);
    },
    onSuccess: async ({ vehicle: saved }) => {
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      await queryClient.invalidateQueries({ queryKey: ['vehicle', saved.id] });
      onClose();
      onSaved?.(saved);
    },
  });

  const valid = form.plate.replace(/[^a-z0-9]/gi, '').length >= 2;
  const set = (key: keyof Form) => (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={vehicle ? t('vehicles.editTitle', { plate: vehicle.plate }) : t('vehicles.add')}
      description={vehicle ? undefined : t('vehicles.addHint')}
      footer={
        <>
          <button
            type="button"
            className="btn-primary"
            disabled={!valid || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('common.saving') : t('common.save')}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          {save.error ? (
            <p role="alert" className="text-sm text-danger ms-auto">
              {save.error instanceof ApiError ? save.error.message : t('common.saveError')}
            </p>
          ) : null}
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={`${t('vehicles.plate')} *`}>
          <input
            className="input font-mono uppercase tracking-wide"
            autoFocus
            maxLength={16}
            value={form.plate}
            placeholder="AB123CD"
            onChange={set('plate')}
          />
        </Field>
        <Field label={t('vehicles.name')} hint={t('vehicles.nameHint')}>
          <input className="input" maxLength={120} value={form.name} onChange={set('name')} />
        </Field>
        <Field label={t('vehicles.make')}>
          <input className="input" maxLength={80} value={form.make} placeholder="Fiat" onChange={set('make')} />
        </Field>
        <Field label={t('vehicles.model')}>
          <input className="input" maxLength={80} value={form.model} placeholder="Doblò" onChange={set('model')} />
        </Field>
        <Field label={t('vehicles.year')}>
          <input className="input" inputMode="numeric" maxLength={4} value={form.year} onChange={set('year')} />
        </Field>
        <Field label={t('vehicles.fuel')}>
          <select className="input" value={form.fuel} onChange={set('fuel')}>
            <option value="">—</option>
            {FUELS.map((f) => (
              <option key={f} value={f}>
                {t(`vehicles.fuels.${f}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('vehicles.registeredOn')}>
          <input className="input" type="date" value={form.registeredOn} onChange={set('registeredOn')} />
        </Field>
        <Field label={t('vehicles.km')}>
          <input className="input" inputMode="numeric" value={form.odometerKm} onChange={set('odometerKm')} />
        </Field>
        <div className="sm:col-span-2">
          <Field label={t('vehicles.vin')}>
            <input className="input font-mono uppercase" maxLength={40} value={form.vin} onChange={set('vin')} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label={t('vehicles.notes')}>
            <textarea className="input h-20 py-2" maxLength={4000} value={form.notes} onChange={set('notes')} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
